import type { PractitionerAdd } from '@common/interfaces';
import type { IIdentifierRegistrationCodes } from '../../common/validation/practitioner';
import type { ValidationErrors } from './validation';
import { ValidationErrorCode } from '../../common/enums/validationErrorCode';
import { padStartBigCode } from '../../common/utils/padStartBigCode';
import { fetchJson } from '../store/fetch';
import { emptyValidationErrors } from './validation';

export async function isRegistrationCodeDuplicated(
    practitioner: Partial<PractitionerAdd & IIdentifierRegistrationCodes>,
    registrationCode: keyof IIdentifierRegistrationCodes,
    practitionerId?: string
) {
    if (practitioner[registrationCode]) {
        const result = await fetchJson('/api/practitioners/list', 'POST', {
            filters: {
                excludeId: practitionerId,
                hasActiveEmployment: true,
                [registrationCode]: practitioner[registrationCode],
            },
        });

        return result.json.total > 0;
    }

    return false;
}

export function getValidationErrorForRegistrationCode(registrationCode: keyof IIdentifierRegistrationCodes) {
    switch (registrationCode) {
        case 'identifierDeclaration':
            return ValidationErrorCode.IDENTIFIER_DECLARATION_EXISTS;
        case 'identifierRegistrationBig':
            return ValidationErrorCode.IDENTIFIER_REGISTRATION_BIG_EXISTS;
        case 'identifierRegistrationKp':
            return ValidationErrorCode.IDENTIFIER_REGISTRATION_KP_EXISTS;
        default:
            return;
    }
}

export async function checkDuplicatedRegistrationCodes(
    practitioner: Partial<PractitionerAdd & IIdentifierRegistrationCodes>,
    practitionerId?: string,
    codesToValidate: (keyof IIdentifierRegistrationCodes)[] = [
        'identifierDeclaration',
        'identifierRegistrationBig',
        'identifierRegistrationKp',
    ]
) {
    const validationErrors = emptyValidationErrors;

    if (
        codesToValidate.includes('identifierDeclaration') &&
        practitioner.identifierDeclaration &&
        (await isRegistrationCodeDuplicated(practitioner, 'identifierDeclaration', practitionerId))
    ) {
        validationErrors.set(
            'practitioner.identifierDeclaration',
            getValidationErrorForRegistrationCode('identifierDeclaration')
        );
    }

    if (codesToValidate.includes('identifierRegistrationBig') && practitioner.identifierRegistrationBig) {
        const practitionerUpdated = {
            ...practitioner,
            identifierRegistrationBig: padStartBigCode(practitioner.identifierRegistrationBig),
        };

        const isDuplicated = await isRegistrationCodeDuplicated(
            practitionerUpdated,
            'identifierRegistrationBig',
            practitionerId
        );

        if (isDuplicated) {
            validationErrors.set(
                'practitioner.identifierRegistrationBig',
                getValidationErrorForRegistrationCode('identifierRegistrationBig')
            );
        }
    }

    if (
        codesToValidate.includes('identifierRegistrationKp') &&
        practitioner.identifierRegistrationKp &&
        (await isRegistrationCodeDuplicated(practitioner, 'identifierRegistrationKp', practitionerId))
    ) {
        validationErrors.set(
            'practitioner.identifierRegistrationKp',
            getValidationErrorForRegistrationCode('identifierRegistrationKp')
        );
    }

    return validationErrors;
}

export function areThereRegistrationCodeErrors(
    errors: ValidationErrors,
    codesToValidate: (keyof IIdentifierRegistrationCodes)[]
) {
    return codesToValidate.some((code) => errors.has(`practitioner.${code}`));
}
